"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { useModal } from "@/hooks/useModal";
import { TUTORIAL_ID } from "../appInfo";

/**
 * The first-run overlay: labels pointing at the parts of the interface, over a
 * dimmed map.
 *
 * Dismissed by any click or key — it is one screen of annotations with nothing to
 * interact with, so anything the user does next means "I've read it". Legacy
 * listened for Enter and Tab only, which left Escape and every other key doing
 * nothing on an overlay that had swallowed the whole viewport.
 */
export function Tutorial() {
  const t = useTranslations("tutorial");
  const { isOpen, close } = useModal(TUTORIAL_ID);

  const ref = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    ref.current?.focus();

    return () => restoreFocusTo.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={t("navigator")}
      tabIndex={0}
      onClick={close}
      onKeyDown={close}
      className="absolute top-0 z-[9999] h-screen w-full whitespace-pre-line bg-black bg-opacity-60 text-base font-extrabold text-white"
    >
      <div className="flex h-screen flex-row">
        {/* Sized to the sidebar it annotates. */}
        <div className="flex w-[34.17%] min-w-[400px] max-w-[700px] shrink-0 items-center justify-center">
          <span>{t("pois")}</span>
        </div>

        <div className="relative flex w-full flex-col">
          <div className="mt-14 flex w-full flex-row justify-between text-center">
            <span className="mx-auto">{t("filter")}</span>
            <span className="mr-10 mt-6">
              <p className="pb-2.5">{t("zoom-line-1")}</p>
              <p>{t("zoom-line-2")}</p>
            </span>
          </div>

          <div className="absolute flex h-screen w-full flex-col items-center justify-center text-center">
            <p className="pb-2.5">{t("map-line-1")}</p>
            <p>{t("map-line-2")}</p>
          </div>

          <div>
            <div className="absolute bottom-20 left-20">{t("basemap")}</div>
            <div className="absolute bottom-24 right-14">{t("navigator")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
